import {
  EscalaCalendarioComponent,
  EscalaSlotEvent,
  EfetivoDoPosto,
  CelulaEscala,
} from '@admin/feactures-modules/postos/posto-detail/tabs/posto-escala-tab/escala-calendario/escala-calendario.component';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Efetivo } from '@core/models';
import {
  EfetivoService,
  EscalaService,
  OcorrenciaService,
} from '@core/services/domain-services';
import { FaltaService, FaltaCreateDto } from '@core/services/falta.service';
import { GeneroEnum, EstadoEfetivoEnum } from '@core/models/models';
import { AnexoService } from '@shared/api/services/anexo.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { finalize, timeout } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { EscalaHistoricoComponent } from '@shared/components/escala-historico/escala-historico.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-ficha-efetivo-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DrawerModule,
    SkeletonModule,
    TooltipModule,
    TabsModule,
    ButtonModule,
    EscalaCalendarioComponent,
    DialogModule,
    FormsModule,
    SelectModule,
    TextareaModule,
    EscalaHistoricoComponent,
    PaginatorControlComponent,
  ],
  providers: [AnexoService],
  templateUrl: './ficha-efetivo-drawer.component.html',
  styleUrl: './ficha-efetivo-drawer.component.scss',
})
export class FichaEfetivoDrawerComponent implements OnChanges {
  @Input() efetivoId: number | null = null;
  @Input() visible = false;
  @Input() pendingAction: 'suspender' | 'afastar' | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() editarClicked = new EventEmitter<number>();
  @Output() verPerfilCompleto = new EventEmitter<number>();
  @Output() statusChanged = new EventEmitter<void>();

  private pendingActionHandled = false;

  private readonly efetivoSvc = inject(EfetivoService);
  private readonly escalaSvc = inject(EscalaService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly msgService = inject(MessageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly faltaSvc = inject(FaltaService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  efetivo: any = null;
  loading = false;
  activeTab = '0';

  ocorrencias: any[] = [];
  loadingOcorrencias = false;

  historicoAcoes: any[] = [];
  totalRondas = 0;
  totalRondasPrevistas = 0;
  totalRondasRealizadas = 0;
  totalOcorrenciasRegistradas = 0;
  loadingHistorico = false;
  loadingStats = false;
  ocorrenciasDaEscala: any[] = [];
  loadingOcorrenciasDaEscala = false;
  ocorrenciasEscalaPage = 1;
  ocorrenciasEscalaPageSize = 5;
  ocorrenciasEscalaTotal = 0;
  private closeCleanupTimer: ReturnType<typeof setTimeout> | null = null;

  get ocorrenciasEscalaFirst(): number {
    return (this.ocorrenciasEscalaPage - 1) * this.ocorrenciasEscalaPageSize;
  }
  escalaSelecionada: any = null;
  escalaSelecionadaData: Date | null = null;
  escalaSelecionadaId: number | null = null;
  escalaSelecionadaCell: CelulaEscala | null = null;
  historicoEscalaData: any = null;
  categoriaSelecionada:
    | 'horario'
    | 'efectividade'
    | 'assiduidade'
    | 'ausencia' = 'horario';

  get escalaIdValida(): boolean {
    return (
      this.escalaSelecionadaId !== null ||
      this.escalaSelecionadaCell !== null ||
      this.historicoEscalaData !== null
    );
  }

  get escalaSelecionadaTurno(): string {
    if (this.historicoEscalaData?.turnoDesignacao) {
      return this.historicoEscalaData.turnoDesignacao;
    }
    if (this.escalaSelecionadaCell?.nomeTurno) {
      return this.escalaSelecionadaCell.nomeTurno;
    }
    if (!this.escalaSelecionada) return '';
    return (
      this.escalaSelecionada.turnoDesignacao ??
      this.escalaSelecionada.nomeTurno ??
      this.escalaSelecionada.turno?.nome ??
      this.escalaSelecionada.turno ??
      'Turno'
    );
  }

  get escalaSelecionadaDataStr(): string {
    const data =
      this.historicoEscalaData?.dataInicio ??
      this.escalaSelecionadaCell?.data ??
      this.escalaSelecionadaData ??
      this.getEscalaInicio(this.escalaSelecionada);
    if (!data) return '';
    return this.formatDate(data);
  }

  get escalaSelecionadaCabecalho(): string {
    if (
      !this.escalaSelecionadaId &&
      !this.escalaSelecionada &&
      !this.escalaSelecionadaCell &&
      !this.historicoEscalaData
    ) {
      return 'HISTORICO RECENTE DA ESCALA';
    }
    const turno = this.escalaSelecionadaTurno || 'Turno';
    const dataStr = this.escalaSelecionadaDataStr || '—';
    return `HISTORICO RECENTE DA ESCALA [${turno}] - [${dataStr}]`;
  }

  get escalaSelecionadaStatusLabel(): string {
    return (
      this.historicoEscalaData?.estadoEscala ??
      this.escalaSelecionadaCell?.estadoLabel ??
      ''
    );
  }

  get escalaSelecionadaStatusClasse(): string {
    const op = this.escalaSelecionadaCell?.estadoOperacional;
    switch (op) {
      case 'presente':
        return 'op-presente';
      case 'concluido':
        return 'op-concluido';
      case 'atraso':
        return 'op-atraso';
      case 'falta':
        return 'op-falta';
      case 'planeado':
        return 'op-planeado';
      case 'folga':
        return 'op-folga';
      default:
        return 'op-default';
    }
  }

  get escalaSelecionadaStatusIcone(): string {
    const op = this.escalaSelecionadaCell?.estadoOperacional;
    switch (op) {
      case 'presente':
        return 'ri-shield-check-line';
      case 'concluido':
        return 'ri-checkbox-circle-line';
      case 'atraso':
        return 'ri-time-line';
      case 'falta':
        return 'ri-user-unfollow-line';
      case 'folga':
        return 'ri-sun-line';
      default:
        return 'ri-calendar-event-line';
    }
  }

  get escalaSelecionadaEntrada(): string | null {
    const targetEfetivoId = Number(this.efetivo?.id ?? this.efetivoId);
    const ef = Array.isArray(this.historicoEscalaData?.efetivos)
      ? this.historicoEscalaData.efetivos.find(
          (e: any) => Number(e.efetivoId) === targetEfetivoId,
        )
      : null;
    if (ef?.horaInicioReal) {
      return this.formatTimeOnly(ef.horaInicioReal);
    }
    return this.escalaSelecionadaCell?.horaEntrada || null;
  }

  get escalaSelecionadaSaida(): string | null {
    const targetEfetivoId = Number(this.efetivo?.id ?? this.efetivoId);
    const ef = Array.isArray(this.historicoEscalaData?.efetivos)
      ? this.historicoEscalaData.efetivos.find(
          (e: any) => Number(e.efetivoId) === targetEfetivoId,
        )
      : null;
    if (ef?.horaFimReal) {
      return this.formatTimeOnly(ef.horaFimReal);
    }
    return this.escalaSelecionadaCell?.horaSaida || null;
  }

  get escalaSelecionadaMotivoFalta(): string | null {
    return this.escalaSelecionadaCell?.motivoFalta || null;
  }

  get eventosOperacionais(): Array<{
    tipo: string;
    titulo: string;
    detalhes?: string;
    hora?: string;
    icone: string;
    cor: string;
  }> {
    const eventos: Array<{
      tipo: string;
      titulo: string;
      detalhes?: string;
      hora?: string;
      icone: string;
      cor: string;
    }> = [];

    if (this.escalaSelecionadaCell?.estadoOperacional === 'falta') {
      eventos.push({
        tipo: 'falta',
        titulo: 'Ausência / Falta Registada',
        detalhes: this.escalaSelecionadaCell.motivoFalta
          ? `Motivo: ${this.escalaSelecionadaCell.motivoFalta}`
          : 'Falta não justificada',
        hora: this.escalaSelecionadaCell.horario || undefined,
        icone: 'ri-user-unfollow-line',
        cor: 'red',
      });
    }

    if (this.escalaSelecionadaCell?.horaEntrada) {
      eventos.push({
        tipo: 'entrada',
        titulo: 'Entrada no Posto (Check-in)',
        detalhes: `Entrada registada às ${this.escalaSelecionadaCell.horaEntrada}`,
        hora: this.escalaSelecionadaCell.horaEntrada,
        icone: 'ri-login-box-line',
        cor: 'green',
      });
    }

    if (this.historicoAcoes.length) {
      for (const acao of this.historicoAcoes) {
        eventos.push({
          tipo: 'acao',
          titulo: acao.acao || 'Registo Operacional',
          detalhes: acao.detalhes || undefined,
          hora: acao.dataRegisto
            ? new Date(acao.dataRegisto).toLocaleTimeString('pt-AO', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            : undefined,
          icone: 'ri-record-circle-line',
          cor: 'blue',
        });
      }
    }

    if (this.escalaSelecionadaCell?.horaSaida) {
      eventos.push({
        tipo: 'saida',
        titulo: 'Saída do Posto (Check-out)',
        detalhes: `Saída registada às ${this.escalaSelecionadaCell.horaSaida}`,
        hora: this.escalaSelecionadaCell.horaSaida,
        icone: 'ri-logout-box-line',
        cor: 'purple',
      });
    }

    if (
      !eventos.length &&
      this.escalaSelecionadaCell?.estadoOperacional === 'planeado'
    ) {
      eventos.push({
        tipo: 'planeado',
        titulo: 'Turno Planeado',
        detalhes: `Horário previsto: ${this.escalaSelecionadaCell.horario || 'Definido na escala'}`,
        icone: 'ri-time-line',
        cor: 'cyan',
      });
    }

    return eventos;
  }

  get escalaSelecionadaTitulo(): string {
    if (!this.escalaSelecionada && !this.escalaSelecionadaCell)
      return 'Sem escala selecionada';

    const turno = this.escalaSelecionadaTurno || 'Escala';
    const dataStr = this.escalaSelecionadaDataStr;

    return dataStr ? `${turno} - ${dataStr}` : String(turno);
  }

  get escalaSelecionadaHorario(): string {
    if (
      this.historicoEscalaData?.turnoHoraInicio &&
      this.historicoEscalaData?.turnoHoraFim
    ) {
      const inicio = this.formatTimeOnly(
        this.historicoEscalaData.turnoHoraInicio,
      );
      const fim = this.formatTimeOnly(this.historicoEscalaData.turnoHoraFim);
      return `${inicio} - ${fim}`;
    }
    if (this.escalaSelecionadaCell?.horario) {
      return this.escalaSelecionadaCell.horario;
    }
    const inicio = this.getEscalaInicio(this.escalaSelecionada);
    const fim = this.getEscalaTermino(this.escalaSelecionada);
    const fmt = (date: Date) =>
      date.toLocaleTimeString('pt-AO', {
        hour: '2-digit',
        minute: '2-digit',
      });

    if (inicio && fim) return `${fmt(inicio)} - ${fmt(fim)}`;
    if (inicio) return fmt(inicio);
    return 'Horario por definir';
  }

  get estatisticasEscala(): any {
    return this.historicoEscalaData?.estatisticas ?? null;
  }

  get rondasEscala(): any[] {
    return Array.isArray(this.historicoEscalaData?.rondas)
      ? this.historicoEscalaData.rondas
      : [];
  }

  get alimentacoesEscala(): any[] {
    return Array.isArray(this.historicoEscalaData?.alimentacoes)
      ? this.historicoEscalaData.alimentacoes
      : [];
  }

  get anexosEscala(): any[] {
    return Array.isArray(this.historicoEscalaData?.anexos)
      ? this.historicoEscalaData.anexos
      : [];
  }

  get efetivosEscalaList(): any[] {
    return Array.isArray(this.historicoEscalaData?.efetivos)
      ? this.historicoEscalaData.efetivos
      : [];
  }

  get verificacoesEquipamentosEscala(): any[] {
    return Array.isArray(this.historicoEscalaData?.verificacoesEquipamentos)
      ? this.historicoEscalaData.verificacoesEquipamentos
      : [];
  }

  getAnexoPreviewUrl(anexoId: number | string): string {
    return `${environment.apiurl}/api/Anexos/preview/${anexoId}`;
  }

  formatDateTime(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleString('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  semanaInicio = this.getMonday(new Date());
  drawerEfetivos: EfetivoDoPosto[] = [];
  escalasSemana: any[] = [];
  loadingEscalas = false;

  loadActive = false;

  displayAfastar = false;
  motivoAfastamento = '';
  loadingAfastar = false;

  // ── ESTADO & MÉTODOS DE SUSPENSÃO DE EFETIVO ──
  displaySuspender = false;
  dataSuspensao = '';
  motivoSuspensao: number | null = null;
  outroMotivo = '';
  observacaoSuspensao = '';
  loadingSuspender = false;

  opcoesMotivoSuspensao = [
    { label: 'Processo Disciplinar', value: 1 },
    { label: 'Incorreção de Conduta / Faltas Graves', value: 2 },
    { label: 'Medida Preventiva / Investigação', value: 3 },
    { label: 'Incumprimento do Regulamento Interno', value: 4 },
    { label: 'Outro Motivo', value: 5 },
  ];

  get emServico(): boolean {
    if (!this.postAtivo) return false;
    const estadoEfetivo = this.getEstadoEfetivo(this.efetivo);
    if (
      estadoEfetivo !== null &&
      estadoEfetivo !== EstadoEfetivoEnum.ATIVO
    ) {
      return false;
    }
    if (
      this.efetivo?.isCheckIn === true ||
      this.efetivo?.emServico === true ||
      this.efetivo?.estaEmServico === true ||
      this.escalaActiva?.isCheckIn === true ||
      this.escalaActiva?.estaEmServico === true
    ) {
      return true;
    }

    const escalas =
      this.efetivo?.escalasPostos?.escalas ?? this.escalasSemana ?? [];
    if (Array.isArray(escalas) && escalas.length > 0) {
      return escalas.some(
        (esc: any) =>
          esc.isCheckIn === true ||
          esc.estaEmServico === true ||
          !!esc.dataCheckIn ||
          !!esc.horaInicioReal ||
          !!esc.horaCheckIn ||
          esc.estadoPresenca === 'Presente' ||
          esc.estadoOperacional === 'presente' ||
          esc.estadoOperacional === 'atraso',
      );
    }
    return false;
  }

  marcarFaltaNoDrawer(): void {
    if (this.emServico) {
      this.msgService.add({
        severity: 'warn',
        summary: 'Ação Bloqueada',
        detail:
          'Não é possível marcar falta porque o efectivo já deu entrada neste turno.',
        life: 4000,
      });
      return;
    }
    const efetivo = this.efetivo;
    if (!efetivo) return;

    const escalaEfetivoId =
      this.escalaActiva?.escalaEfetivoId ||
      this.escalaActiva?.id ||
      efetivo.escalaEfetivoId ||
      efetivo.escalasPostos?.id ||
      0;

    this.confirmSvc.confirm({
      header: 'Marcar Falta',
      message: `Registar falta de "${efetivo.nome}" para o turno de hoje (${new Date().toLocaleDateString('pt-AO')})?`,
      icon: 'pi pi-calendar-times',
      acceptLabel: 'Confirmar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        const d = new Date();
        const hoje = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const payload: FaltaCreateDto = {
          escalaEfetivoId: Number(escalaEfetivoId),
          dataFaltaInicio: hoje,
          dataFaltaFim: hoje,
          justificada: false,
          tipoFaltaEnum: 1,
          observacao: `Falta marcada na Ficha do Efectivo ${efetivo.nome}`,
        };

        this.loadActive = true;
        this.faltaSvc
          .create(payload)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadActive = false;
              this.cdr.markForCheck();
            }),
          )
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Falta Registada',
                detail: `Falta de ${efetivo.nome} registada com sucesso.`,
                life: 4000,
              });
              this.loadData();
            },
            error: (err) => {
              this.msgService.add({
                severity: 'error',
                summary: 'Erro ao Registar Falta',
                detail:
                  err?.error?.mensagem ||
                  'Não foi possível registar a falta. Verifique os dados.',
                life: 4000,
              });
            },
          });
      },
    });
  }

  get escalaActiva(): any {
    return this.efetivo?.escalasPostos ?? null;
  }

  get postAtivo(): any {
    return this.efetivo?.escalasPostos?.posto ?? null;
  }

  get fotoUrl(): string | null {
    const url = this.efetivo?.urlFotografia;
    if (!url) return null;
    const versao = this.efetivo?.dataAtualizacao ?? Date.now();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${encodeURIComponent(versao)}`;
  }

  getEstadoEfetivo(efetivo: any): number | null {
    if (!efetivo) return null;

    const rawEstado =
      efetivo.estadoEfetivo !== undefined && efetivo.estadoEfetivo !== null
        ? efetivo.estadoEfetivo
        : efetivo.estado;

    if (rawEstado === undefined || rawEstado === null || rawEstado === '') {
      return null;
    }

    const estadoNumerico = Number(rawEstado);
    if (!Number.isNaN(estadoNumerico)) return estadoNumerico;

    const estados: Record<string, number> = {
      ATIVO: EstadoEfetivoEnum.ATIVO,
      ACTIVO: EstadoEfetivoEnum.ATIVO,
      INATIVO: EstadoEfetivoEnum.INATIVO,
      INACTIVO: EstadoEfetivoEnum.INATIVO,
      AFASTADO: EstadoEfetivoEnum.AFASTADO,
      SUSPENSO: EstadoEfetivoEnum.SUSPENSO,
      SUSPENSOS: EstadoEfetivoEnum.SUSPENSO,
      'DE FOLGA': EstadoEfetivoEnum.DE_FOLGA,
      DE_FOLGA: EstadoEfetivoEnum.DE_FOLGA,
      FÉRIAS: EstadoEfetivoEnum.FERIAS,
      FERIAS: EstadoEfetivoEnum.FERIAS,
    };

    return estados[String(rawEstado).trim().toUpperCase()] ?? null;
  }

  isEfetivoAtivo(efetivo: any): boolean {
    if (!efetivo) return false;
    const rawEst =
      efetivo.estadoEfetivo !== undefined && efetivo.estadoEfetivo !== null
        ? efetivo.estadoEfetivo
        : efetivo.estado;

    if (rawEst === undefined || rawEst === null || rawEst === '') return false;

    const numState = Number(rawEst);
    if (!isNaN(numState)) {
      return (
        numState === EstadoEfetivoEnum.ATIVO ||
        numState === EstadoEfetivoEnum.DE_FOLGA ||
        numState === EstadoEfetivoEnum.FERIAS
      );
    }

    const est = String(rawEst).trim().toUpperCase();
    return (
      est === 'ATIVO' ||
      est === 'ACTIVO' ||
      est === 'DE FOLGA' ||
      est === 'FÉRIAS' ||
      est === 'FERIAS' ||
      est === '0' ||
      est === '4' ||
      est === '5'
    );
  }

  get totalEscalas(): number {
    return Number(
      this.efetivo?.totalEscalas ??
        this.efetivo?.escalasPostos?.escalas?.length ??
        this.escalasSemana.length,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.closeCleanupTimer) {
      clearTimeout(this.closeCleanupTimer);
      this.closeCleanupTimer = null;
    }

    if (this.visible && this.efetivoId) {
      if (!this.efetivo || Number(this.efetivo.id) !== this.efetivoId) {
        this.loadData();
      } else {
        this.tryOpenPendingAction();
      }
    }

    if (changes['visible'] && !this.visible) {
      if (this.closeCleanupTimer) clearTimeout(this.closeCleanupTimer);
      this.closeCleanupTimer = setTimeout(() => {
        if (!this.visible) {
          this.clearDrawerState();
          this.pendingActionHandled = false;
          this.cdr.markForCheck();
        }
        this.closeCleanupTimer = null;
      }, 300);
    }
  }

  loadData(): void {
    if (!this.efetivoId) return;

    this.loading = true;
    this.efetivo = null;
    this.cdr.markForCheck();

    this.efetivoSvc
      .getById(this.efetivoId)
      .pipe(timeout({ first: 15000 }), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.efetivo = response?.data ?? response;
          this.loading = false;
          this.semanaInicio = this.getMonday(new Date());
          this.drawerEfetivos = [this.toCalendarEfetivo(this.efetivo)];
          this.loadEscalasSemana();
          this.loadOcorrencias();
          this.tryOpenPendingAction();
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar a ficha do efectivo.',
            life: 3500,
          });
          this.cdr.markForCheck();
        },
      });
  }

  private tryOpenPendingAction(): void {
    if (
      !this.visible ||
      !this.efetivo ||
      !this.pendingAction ||
      this.pendingActionHandled
    ) {
      return;
    }

    this.pendingActionHandled = true;
    if (this.pendingAction === 'suspender') {
      this.abrirSuspender(this.efetivo);
    } else {
      this.abrirAfastar(this.efetivo);
    }
  }

  loadOcorrencias(): void {
    if (!this.efetivoId) return;

    this.loadingOcorrencias = true;
    this.ocorrenciaSvc
      .getAll({ idEfetivo: this.efetivoId, pageSize: 10 })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingOcorrencias = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.ocorrencias = this.readData(response);
        },
        error: () => {
          this.ocorrencias = [];
        },
      });
  }

  loadEstatisticasENovoFluxo(): void {
    const efetivoId = this.efetivo?.id ?? this.efetivoId;
    const escalaId =
      this.escalaSelecionadaId ?? this.getEscalaId(this.escalaSelecionada);

    if (!efetivoId || !escalaId) {
      this.resetHistoricoEscala();
      this.cdr.markForCheck();
      return;
    }

    this.loadingHistorico = true;
    this.loadingStats = true;

    this.efetivoSvc
      .obterEstatisticasEfetivo(efetivoId, escalaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingHistorico = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const data = this.readPayload(res);
          this.historicoAcoes = Array.isArray(data?.historicoAcoes)
            ? data.historicoAcoes
            : [];
          this.totalRondas = Number(data?.totalRondas ?? 0);
        },
        error: () => {
          this.historicoAcoes = [];
          this.totalRondas = 0;
        },
      });

    this.efetivoSvc
      .obterEstatisticasEfetivo(efetivoId, escalaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingStats = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const data = this.readPayload(res);
          this.totalRondasPrevistas = Number(data?.totalRondasPrevistas ?? 0);
          this.totalRondasRealizadas = Number(data?.totalRondasRealizadas ?? 0);
          this.totalOcorrenciasRegistradas = Number(
            data?.totalOcorrenciasRegistradas ?? 0,
          );
        },
        error: () => {
          this.totalRondasPrevistas = 0;
          this.totalRondasRealizadas = 0;
          this.totalOcorrenciasRegistradas = 0;
        },
      });
  }

  onWeekChange(date: Date): void {
    this.semanaInicio = this.getMonday(date);
    this.loadEscalasSemana();
  }

  onEscalaSlotClick(event: EscalaSlotEvent): void {
    if (event.categoria) {
      this.categoriaSelecionada = event.categoria;
    }
    if (!event.escala) {
      this.escalaSelecionada = null;
      this.escalaSelecionadaData = new Date(event.data);
      this.escalaSelecionadaId = null;
      this.escalaSelecionadaCell = event.cell ?? null;
      this.resetHistoricoEscala();
      this.cdr.markForCheck();
      return;
    }

    this.selecionarEscala(event.escala, event.data, event.cell);
  }

  selecionarCategoria(
    categoria: 'horario' | 'efectividade' | 'assiduidade' | 'ausencia',
  ): void {
    this.categoriaSelecionada = categoria;
    this.cdr.markForCheck();
  }

  get assiduidadeStatusCalculado(): {
    titulo: string;
    descricao: string;
    classe: string;
    icone: string;
  } {
    if (!this.escalaSelecionadaCell) {
      return {
        titulo: 'Sem informação',
        descricao: 'Selecione um turno',
        classe: 'op-default',
        icone: 'ri-question-line',
      };
    }
    const cell = this.escalaSelecionadaCell;
    if (cell.estadoOperacional === 'falta') {
      return {
        titulo: 'Falta / Ausência Registada',
        descricao: cell.motivoFalta
          ? `Motivo: ${cell.motivoFalta}`
          : 'Falta não justificada',
        classe: 'op-falta',
        icone: 'ri-user-unfollow-line',
      };
    }
    if (cell.horaEntrada) {
      if (cell.estadoOperacional === 'atraso') {
        const previsto = cell.horario ? cell.horario.split(' - ')[0] : '06:00';
        return {
          titulo: 'Entrada Tardia (Atraso)',
          descricao: `Entrada registada às ${cell.horaEntrada} (após horário previsto ${previsto})`,
          classe: 'op-atraso',
          icone: 'ri-alarm-warning-line',
        };
      }
      return {
        titulo: 'Assiduidade Regular / Pontual',
        descricao: `Entrada realizada no horário previsto às ${cell.horaEntrada}`,
        classe: 'op-presente',
        icone: 'ri-checkbox-circle-line',
      };
    }
    return {
      titulo: 'Assiduidade Pendente',
      descricao: 'Será calculada automaticamente após o registo de entrada',
      classe: 'op-planeado',
      icone: 'ri-time-line',
    };
  }

  get tipoTurnoExtenso(): string {
    if (!this.escalaSelecionadaCell) return 'Diurno';
    const tipo = this.escalaSelecionadaCell.tipo;
    switch (tipo) {
      case 'N':
        return 'Noturno';
      case '24':
        return '24 Horas';
      case 'R':
        return 'Reserva';
      case 'F':
        return 'Folga';
      default:
        return 'Diurno';
    }
  }

  getInitials(nome?: string): string {
    if (!nome) return '?';
    return nome
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  formatDate(value: string | Date | undefined): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('pt-AO');
  }

  getEstadoBadgeClass(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'estado-ativo',
      1: 'estado-inativo',
      2: 'estado-afastado',
      3: 'estado-suspenso',
      4: 'estado-folga',
      5: 'estado-ferias',
    };
    return numericState !== null
      ? (map[numericState] ?? 'estado-inativo')
      : 'estado-inativo';
  }

  getEstadoIcon(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'pi pi-check-circle',
      1: 'pi pi-ban',
      2: 'pi pi-minus-circle',
      3: 'pi pi-exclamation-circle',
      4: 'pi pi-clock',
      5: 'pi pi-calendar',
    };
    return numericState !== null
      ? (map[numericState] ?? 'pi pi-minus')
      : 'pi pi-minus';
  }

  getEstadoLabel(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'Ativo',
      1: 'Inativo',
      2: 'Afastado',
      3: 'Suspenso',
      4: 'De Folga',
      5: 'Férias',
    };
    return numericState !== null ? (map[numericState] ?? '—') : '—';
  }

  getGeneroLabel(g: number | string | null | undefined): string {
    if (g === null || g === undefined || g === '') return '—';
    const valStr = String(g).toUpperCase();
    if (valStr === '1' || valStr === 'MASCULINO') return 'Masculino';
    if (valStr === '2' || valStr === 'FEMININO') return 'Feminino';
    return valStr || '—';
  }

  getCriticidadeClass(criticidade: string): string {
    const map: Record<string, string> = {
      URGENTE: 'crit-urgente',
      ALTA: 'crit-alta',
      MEDIA: 'crit-media',
    };
    return map[criticidade] || '';
  }

  getCriticidadeLabel(criticidade: string): string {
    const map: Record<string, string> = {
      URGENTE: 'Urgente',
      ALTA: 'Alta',
      MEDIA: 'Média',
    };
    return map[criticidade] || criticidade || '—';
  }

  getTipoOcorrenciaLabel(tipo: string): string {
    const map: Record<string, string> = {
      INTRUSAO: 'Intrusão',
      AUSENCIA: 'Ausência',
      AVARIA: 'Avaria',
      RONDA_INCOMPLETA: 'Ronda Incompleta',
      SUSPEITO: 'Suspeito',
      ACIDENTE: 'Acidente',
      OUTRO: 'Outro',
    };
    return map[tipo] || tipo || '—';
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cdr.markForCheck();
  }

  abrirFichaCompleta(): void {
    if (!this.efetivoId) return;
    const id = this.efetivoId;
    this.onClose();
    this.router.navigate(['/admin/efetivos', id]);
  }

  editarEfetivo(): void {
    if (this.efetivoId) this.editarClicked.emit(this.efetivoId);
  }

  ToggleStatus(efetivo: Efetivo): void {
    if (!efetivo) return;
    const id = Number(efetivo.id);
    this.confirmSvc.confirm({
      header: 'Desactivar Efectivo',
      message: `O efectivo "${efetivo.nome}" será desactivado e deixará de poder ser escalado. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Desactivar',
      rejectLabel: 'Cancelar',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        this.loadActive = true;
        this.efetivoSvc
          .toggleStatus(id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadActive = false;
              this.cdr.markForCheck();
            }),
          )
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Efectivo desactivado com sucesso.',
                life: 3000,
              });
              this.loadData();
              this.statusChanged.emit();
            },
            error: () => {
              this.msgService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Falha ao desactivar efectivo.',
                life: 3000,
              });
            },
          });
      },
    });
  }

  Activar(efetivo: Efetivo): void {
    if (!efetivo) return;
    const id = Number(efetivo.id);
    this.confirmSvc.confirm({
      header: 'Activar Efectivo',
      message: `O efectivo "${efetivo.nome}" será activado e poderá voltar a ser escalado. Deseja continuar?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Activar',
      rejectLabel: 'Cancelar',
      acceptButtonProps: { severity: 'success' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        this.loadActive = true;
        this.efetivoSvc
          .reativar(id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loadActive = false;
              this.cdr.markForCheck();
            }),
          )
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Efectivo activado com sucesso.',
                life: 3000,
              });
              this.loadData();
              this.statusChanged.emit();
            },
            error: () => {
              this.msgService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Falha ao activar efectivo.',
                life: 3000,
              });
            },
          });
      },
    });
  }

  abrirAfastar(efetivo: Efetivo): void {
    if (!efetivo) return;
    this.motivoAfastamento = '';
    this.displayAfastar = true;
  }

  confirmarAfastar(): void {
    if (!this.efetivo || !this.motivoAfastamento.trim()) return;
    const id = Number(this.efetivo.id);
    this.loadingAfastar = true;
    this.efetivoSvc
      .afastar(id, this.motivoAfastamento)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingAfastar = false;
          this.displayAfastar = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Efectivo afastado com sucesso.',
            life: 3000,
          });
          this.loadData();
          this.statusChanged.emit();
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao afastar efectivo.',
            life: 3000,
          });
        },
      });
  }

  abrirSuspender(efetivo: Efetivo): void {
    if (!efetivo) return;
    this.motivoSuspensao = null;
    this.outroMotivo = '';
    this.observacaoSuspensao = '';
    this.displaySuspender = true;
  }

  confirmarSuspender(): void {
    if (!this.efetivo || this.motivoSuspensao === null) return;
    if (this.motivoSuspensao === 5 && !this.outroMotivo.trim()) return;

    const id = Number(this.efetivo.id);
    this.loadingSuspender = true;

    const payload = {
      motivoSuspensao: Number(this.motivoSuspensao),
      outroMotivo: this.motivoSuspensao === 5 ? this.outroMotivo.trim() : null,
      observacao: this.observacaoSuspensao
        ? this.observacaoSuspensao.trim()
        : '',
    };

    this.efetivoSvc
      .suspender(id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingSuspender = false;
          this.displaySuspender = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Efectivo suspenso com sucesso.',
            life: 3000,
          });
          this.loadData();
          this.statusChanged.emit();
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao suspender efectivo.',
            life: 3000,
          });
        },
      });
  }

  private loadEscalasSemana(): void {
    if (!this.efetivoId) return;

    const fimSemana = this.addDays(this.semanaInicio, 6);
    fimSemana.setHours(23, 59, 59, 999);
    this.loadingEscalas = true;

    this.escalaSvc
      .getAll({
        EfetivoId: this.efetivoId,
        DataInicioAte: this.toApiDateTime(fimSemana),
        DataTerminoDe: this.toApiDateTime(this.semanaInicio),
        pageNumber: 1,
        pageSize: 100,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingEscalas = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.escalasSemana = this.readData(response);
          this.sincronizarEscalaSelecionadaComSemana();
        },
        error: () => {
          this.escalasSemana = [];
          this.sincronizarEscalaSelecionadaComSemana();
          this.msgService.add({
            severity: 'error',
            summary: 'Calendário indisponível',
            detail: 'Não foi possível carregar as escalas deste efectivo.',
            life: 3000,
          });
        },
      });
  }

  private toCalendarEfetivo(efetivo: any): EfetivoDoPosto {
    return {
      id: Number(efetivo.id),
      nome: String(efetivo.nome ?? `Efetivo #${efetivo.id}`),
      codigo:
        efetivo.codigo ?? efetivo.numeroAgente ?? efetivo.numeroMecanografico,
      cargo: efetivo.nomeFuncao ?? efetivo.nomeCategoria,
    };
  }

  private readData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.records)) return response.records;
    if (Array.isArray(response?.items)) return response.items;
    return [];
  }

  private readPayload(response: any): any {
    return response?.data && !Array.isArray(response.data)
      ? response.data
      : response;
  }

  private clearDrawerState(): void {
    this.loading = false;
    this.loadingOcorrencias = false;
    this.efetivo = null;
    this.ocorrencias = [];
    this.ocorrenciasDaEscala = [];
    this.drawerEfetivos = [];
    this.escalasSemana = [];
    this.loadingEscalas = false;
    this.escalaSelecionada = null;
    this.escalaSelecionadaData = null;
    this.escalaSelecionadaId = null;
    this.escalaSelecionadaCell = null;
    this.historicoEscalaData = null;
    this.historicoAcoes = [];
    this.totalRondas = 0;
    this.totalRondasPrevistas = 0;
    this.totalRondasRealizadas = 0;
    this.totalOcorrenciasRegistradas = 0;
    this.loadingHistorico = false;
    this.loadingStats = false;
    this.loadingOcorrenciasDaEscala = false;
    this.ocorrenciasEscalaPage = 1;
    this.ocorrenciasEscalaTotal = 0;
  }

  private getMonday(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    const day = result.getDay();
    result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day));
    return result;
  }

  private addDays(date: Date, amount: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
  }

  private toApiDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  }

  private selecionarEscala(
    escala: any,
    data?: Date,
    cell?: CelulaEscala,
  ): void {
    const escalaId = this.getEscalaId(escala);

    if (!escalaId) {
      this.escalaSelecionada = null;
      this.escalaSelecionadaData = data ? new Date(data) : null;
      this.escalaSelecionadaId = null;
      this.escalaSelecionadaCell = cell ?? null;
      this.historicoEscalaData = null;
      this.resetHistoricoEscala();
      this.cdr.markForCheck();
      return;
    }

    this.escalaSelecionada = escala;
    this.escalaSelecionadaData =
      data instanceof Date ? new Date(data) : this.getEscalaInicio(escala);
    this.escalaSelecionadaId = escalaId;
    this.escalaSelecionadaCell = cell ?? null;
    this.ocorrenciasEscalaPage = 1;

    this.carregarHistoricoEscalaPorId(escalaId);
    this.cdr.markForCheck();
  }

  carregarHistoricoEscalaPorId(escalaId: number): void {
    this.loadingHistorico = true;
    this.loadingStats = true;
    this.loadingOcorrenciasDaEscala = true;
    this.historicoEscalaData = null;
    this.cdr.markForCheck();

    this.escalaSvc
      .getHistorico(escalaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingHistorico = false;
          this.loadingStats = false;
          this.loadingOcorrenciasDaEscala = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const payload = res?.data ?? res;
          this.historicoEscalaData = payload;
          this.processarDadosHistoricoEscala(payload);
        },
        error: () => {
          this.loadEstatisticasENovoFluxo();
          this.loadOcorrenciasDaEscala();
        },
      });
  }

  private processarDadosHistoricoEscala(data: any): void {
    if (!data) return;

    const stats = data.estatisticas ?? {};
    this.totalRondas = Number(stats.totalRondas ?? data.rondas?.length ?? 0);
    this.totalRondasPrevistas = Number(
      stats.totalRondas ?? data.rondas?.length ?? 0,
    );
    this.totalRondasRealizadas = Number(stats.totalRondasConcluidas ?? 0);
    this.totalOcorrenciasRegistradas = Number(
      stats.totalOcorrencias ?? data.ocorrencias?.length ?? 0,
    );

    const targetEfetivoId = Number(this.efetivo?.id ?? this.efetivoId);
    const efetivoEscala = Array.isArray(data.efetivos)
      ? (data.efetivos.find(
          (e: any) => Number(e.efetivoId) === targetEfetivoId,
        ) ?? data.efetivos[0])
      : null;

    if (efetivoEscala) {
      if (efetivoEscala.horaInicioReal && this.escalaSelecionadaCell) {
        this.escalaSelecionadaCell.horaEntrada = this.formatTimeOnly(
          efetivoEscala.horaInicioReal,
        );
      }
      if (efetivoEscala.horaFimReal && this.escalaSelecionadaCell) {
        this.escalaSelecionadaCell.horaSaida = this.formatTimeOnly(
          efetivoEscala.horaFimReal,
        );
      }
    }

    const rawOcorrencias = Array.isArray(data.ocorrencias)
      ? data.ocorrencias
      : [];
    this.ocorrenciasDaEscala = rawOcorrencias.map((oc: any) => ({
      ...oc,
      id: oc.id,
      tipoOcorrencia:
        oc.tipoOcorrencia ??
        (oc.tipoOcorrenciaEnum === 5
          ? oc.descricaoTipoOutro || 'Outros'
          : 'Ocorrência'),
      descricao:
        oc.descricaoTipoOutro ?? oc.descricao ?? oc.motivo ?? 'Sem descrição',
      criticidade: oc.criticidade ?? 'Média',
      estado: oc.estadoOcorrencia ?? oc.estado ?? 'Aberta',
      dataOcorrencia: oc.dataOcorrencia,
      rondaId: oc.rondaId,
      imagens: oc.anexosUrls ?? oc.imagens ?? [],
    }));
    this.ocorrenciasEscalaTotal = this.ocorrenciasDaEscala.length;

    const acoes: any[] = [];

    const horaEntradaStr =
      efetivoEscala?.horaInicioReal || this.escalaSelecionadaCell?.horaEntrada;
    if (horaEntradaStr) {
      acoes.push({
        tipo: 'entrada',
        titulo: 'Entrada no Posto (Check-in)',
        detalhes: `Entrada registada às ${this.formatTimeOnly(horaEntradaStr)}`,
        hora: this.formatTimeOnly(horaEntradaStr),
        icone: 'ri-login-box-line',
        cor: 'green',
      });
    }

    const rondas = Array.isArray(data.rondas) ? data.rondas : [];
    for (const r of rondas) {
      const horaStr = r.dataInicio
        ? this.formatTimeOnly(r.dataInicio)
        : undefined;
      const totalP = Number(
        r.totalPontosCriticos ?? r.pontosCriticos?.length ?? 0,
      );
      const totalC = Number(r.totalPontosCriticosChecados ?? 0);
      const concluida = r.concluida === true;
      acoes.push({
        tipo: 'ronda',
        titulo: `Ronda ${concluida ? 'Concluída' : 'em Andamento'} #${r.id}`,
        detalhes: `${totalC}/${totalP} pontos críticos checados${r.efetivoNome ? ' · ' + r.efetivoNome : ''}`,
        hora: horaStr,
        icone: 'ri-route-line',
        cor: concluida ? 'blue' : 'amber',
      });
    }

    const faltas = Array.isArray(efetivoEscala?.faltas)
      ? efetivoEscala.faltas
      : [];
    if (
      faltas.length ||
      this.escalaSelecionadaCell?.estadoOperacional === 'falta'
    ) {
      acoes.push({
        tipo: 'falta',
        titulo: 'Ausência / Falta Registada',
        detalhes:
          this.escalaSelecionadaCell?.motivoFalta ||
          faltas[0]?.motivo ||
          'Falta registada no turno',
        hora: this.escalaSelecionadaCell?.horario || undefined,
        icone: 'ri-user-unfollow-line',
        cor: 'red',
      });
    }

    const horaSaidaStr =
      efetivoEscala?.horaFimReal || this.escalaSelecionadaCell?.horaSaida;
    if (horaSaidaStr) {
      acoes.push({
        tipo: 'saida',
        titulo: 'Saída do Posto (Check-out)',
        detalhes: `Saída registada às ${this.formatTimeOnly(horaSaidaStr)}`,
        hora: this.formatTimeOnly(horaSaidaStr),
        icone: 'ri-logout-box-line',
        cor: 'purple',
      });
    }

    this.historicoAcoes = acoes;
  }

  formatTimeOnly(value: string | Date | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      if (typeof value === 'string' && value.includes(':')) {
        return value.substring(0, 5);
      }
      return String(value);
    }
    return date.toLocaleTimeString('pt-AO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private sincronizarEscalaSelecionadaComSemana(): void {
    if (!this.escalasSemana.length || !this.escalaSelecionadaId) {
      this.escalaSelecionada = null;
      this.escalaSelecionadaData = null;
      this.escalaSelecionadaId = null;
      this.historicoEscalaData = null;
      this.resetHistoricoEscala();
      this.cdr.markForCheck();
      return;
    }

    const escalaAtual = this.escalasSemana.find(
      (escala) => this.getEscalaId(escala) === this.escalaSelecionadaId,
    );

    if (!escalaAtual) {
      this.escalaSelecionada = null;
      this.escalaSelecionadaData = null;
      this.escalaSelecionadaId = null;
      this.historicoEscalaData = null;
      this.resetHistoricoEscala();
      this.cdr.markForCheck();
      return;
    }

    this.selecionarEscala(
      escalaAtual,
      this.getEscalaInicio(escalaAtual) ?? undefined,
    );
  }

  onOcorrenciasEscalaPageChange(event: any): void {
    this.ocorrenciasEscalaPage = (event.page ?? 0) + 1;
    this.ocorrenciasEscalaPageSize =
      event.rows ?? this.ocorrenciasEscalaPageSize;
    this.loadOcorrenciasDaEscala();
  }

  private loadOcorrenciasDaEscala(): void {
    const efetivoId = this.efetivo?.id ?? this.efetivoId;
    const escalaId =
      this.escalaSelecionadaId ?? this.getEscalaId(this.escalaSelecionada);

    if (!efetivoId || !escalaId) {
      this.ocorrenciasDaEscala = [];
      return;
    }

    this.loadingOcorrenciasDaEscala = true;
    this.ocorrenciaSvc
      .getAll({
        idEfetivo: efetivoId,
        idEscala: escalaId,
        IdEscala: escalaId,
        pageNumber: this.ocorrenciasEscalaPage,
        pageSize: this.ocorrenciasEscalaPageSize,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingOcorrenciasDaEscala = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.ocorrenciasDaEscala = this.readData(response);
          this.ocorrenciasEscalaTotal = Number(
            response?.totalRecords ?? this.ocorrenciasDaEscala.length,
          );
        },
        error: () => {
          this.ocorrenciasDaEscala = [];
          this.ocorrenciasEscalaTotal = 0;
        },
      });
  }

  private resetHistoricoEscala(): void {
    this.historicoEscalaData = null;
    this.historicoAcoes = [];
    this.totalRondas = 0;
    this.totalRondasPrevistas = 0;
    this.totalRondasRealizadas = 0;
    this.totalOcorrenciasRegistradas = 0;
    this.ocorrenciasDaEscala = [];
    this.ocorrenciasEscalaPage = 1;
    this.ocorrenciasEscalaTotal = 0;
  }

  private getEscalaId(escala: any): number | null {
    const raw = escala?.escalaId ?? escala?.idEscala ?? escala?.id;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private getEscalaInicio(escala: any): Date | null {
    return this.parseEscalaDate(
      escala?.dataInicioPrevista ??
        escala?.dataInicio ??
        escala?.inicio ??
        escala?.data,
    );
  }

  private getEscalaTermino(escala: any): Date | null {
    return this.parseEscalaDate(
      escala?.dataTerminoPrevista ?? escala?.dataTermino ?? escala?.fim,
    );
  }

  private parseEscalaDate(
    value: string | Date | null | undefined,
  ): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

}
